import { assertUncomputedFromBase, fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { attachInventoryCatalog, inventoryCatalogScope, mergeInventoryMetadata, type InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import type { BaseEnvelope } from '../janus-engine/compile/base-patch';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { createNpcDefinitionFinalizer } from '../janus-engine/compile/from-base-npc-finalization';
import { compileFromBaseEnchantmentCard } from '../janus-engine/compile/from-base-enchantment-rows';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import type {} from './compile-version-recovery';

/** A cold SELECT shell is waiting for its first browser paint, not refusing the reader. */
export function entityBasePending(value: unknown): boolean {
    return fromBaseRecord(value) && (typeof value._entity_refusal === 'string'
        && /^unported:[a-z]+-base-pending$/.test(value._entity_refusal)
        || value._inventory_refusal === 'unported:inventory-base-pending');
}

/** Scene bundles consult the websocket owner's façade, including an enclosing campaign page. */
export async function compileFetchedEntity(value: unknown, inlineCatalog?: InventorySharedCatalog): Promise<Record<string, unknown>> {
    try { return await compileFetchedEntityInput(value, inlineCatalog); }
    catch (error) {
        let owner: Window = window;
        if (!owner.ftHandleCompileEngineMismatch) {
            try { if (window.parent !== window && window.parent.ftHandleCompileEngineMismatch) owner = window.parent; }
            catch { /* Cross-origin host. */ }
        }
        if (owner.ftHandleCompileEngineMismatch?.(error)) throw new Error('engine-version-reload');
        throw error;
    }
}

async function compileFetchedEntityInput(value: unknown, inlineCatalog?: InventorySharedCatalog): Promise<Record<string, unknown>> {
    if (!fromBaseRecord(value)) throw new Error('unported:entity-fetch-shape');
    if (fromBaseRecord(value._random_table_inputs)) {
        const inputs = value._random_table_inputs;
        let owner: Window = window;
        if (!owner.ftGetEntitySharedCatalog && window.parent !== window) {
            try { if (window.parent.ftGetEntitySharedCatalog) owner = window.parent; } catch { /* Cross-origin parent. */ }
        }
        const shared = fromBaseRecord(inputs.shared) ? inputs.shared as unknown as InventorySharedCatalog
            : await owner.ftGetEntitySharedCatalog?.(String(inputs.version));
        if (!shared || inputs.version && shared.version !== inputs.version) throw new Error('inventory-catalog-version');
        inlineCatalog = inventoryCatalogScope(shared, inputs.base as Record<string, unknown>);
        delete value._random_table_inputs;
    }
    if (fromBaseRecord(value._enchantment_template_inputs)) {
        const inputs = value._enchantment_template_inputs;
        assertUncomputedFromBase(inputs.base);
        assertUncomputedFromBase(inputs.shared);
        const shared = inputs.shared as unknown as InventorySharedCatalog;
        const base = inventoryCatalogScope(shared, inputs.base as Record<string, unknown>).base;
        const runtime = entityRuntimeMetadata(shared.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
        const inventory = mergeInventoryMetadata(runtime.inventory, base.inventory_metadata);
        const finalizer = createNpcDefinitionFinalizer(base, runtime.player, inventory);
        const template = compileFromBaseEnchantmentCard(String(readItemField(value, 'item_id')), base, inventory,
            id => finalizer.definition(id, []));
        const result: Record<string, unknown> = { ...value, ...template, items: { ...(fromBaseRecord(value.items) ? value.items : {}),
            item_name: readItemField(template, 'item_name') } };
        delete result._enchantment_template_inputs;
        return result;
    }
    if (!value._entity_base && !value._inventory_base && !value._inventory_held) {
        if (entityBasePending(value)) throw new Error('unported:entity-compile:base-unavailable');
        return value;
    }
    if (inlineCatalog && value._entity_base) {
        const envelope = attachInventoryCatalog({ ...value._entity_base as BaseEnvelope, catalog_version: inlineCatalog.version }, inlineCatalog);
        // This authorized response already carries the entire catalog; no reference read is owed.
        delete envelope.catalog_version;
        value._entity_base = envelope;
    }
    let owner: Window = window;
    if (!owner.ftAdoptInventoryBase) {
        try { if (window.parent !== window && window.parent.ftAdoptInventoryBase) owner = window.parent; }
        catch { /* A cross-origin host cannot own this page's compiler. */ }
    }
    if (!owner.ftAdoptInventoryBase) throw new Error('unported:entity-compile-bridge');
    let result = value;
    // An obsolete job may settle before the newer adoption of this same host. Follow
    // changed inputs into that current job instead of treating its placeholder as a verdict.
    for (;;) {
        const input = [result._entity_base, result._inventory_base, result._inventory_held];
        const adopted = await owner.ftAdoptInventoryBase(result, { transient: !!inlineCatalog, detached: true });
        if (owner.ftBaseCommitsPaused?.()) throw new Error('engine-version-reload');
        if (fromBaseRecord(adopted)) result = adopted;
        const current = [result._entity_base, result._inventory_base, result._inventory_held];
        if (!entityBasePending(result) || input.every((before, index) => before === current[index])) break;
    }
    if (entityBasePending(result)) {
        const envelope = fromBaseRecord(result._entity_base) ? result._entity_base : result._inventory_base;
        const id = fromBaseRecord(envelope) ? String(envelope.entity_id ?? '') : '';
        if (id && await owner.ftAwaitEntityBase?.([id]) === false) throw new Error('unported:entity-base:adoption-timeout');
    }
    if (entityBasePending(result)) {
        owner.ftDiscardInventoryBase?.(value);
        throw new Error('unported:entity-compile:paint-incomplete');
    }
    const refusal = result._entity_refusal ?? result._inventory_refusal;
    if (typeof refusal === 'string') {
        owner.ftDiscardInventoryBase?.(value);
        throw new Error(refusal);
    }
    return result;
}
